import { useEffect, useState } from "react";
import { useApiQuery } from "./useApiQuery";
import { endpointsMetadata } from "../routes/endpointsMetadata";

const VERSION_STORAGE_KEY = "app_version";

interface VersionResponse {
  version: string;
}

export const useVersionCheck = () => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  // Fetch version from backend with 10-minute caching
  const { data: versionData } = useApiQuery<VersionResponse>(
    {
      queryKey: [endpointsMetadata.initialization.getVersion],
      endpoint: endpointsMetadata.initialization.getVersion,
      globalIsLoading: false,
      disableErrorToast: true,
      retry: false,
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes - data is fresh for 10 mins
      cacheTime: 15 * 60 * 1000, // 15 minutes - keep in cache for 15 mins
      refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
      refetchIntervalInBackground: true, // Continue refetching even when window is not focused
    }
  );

  useEffect(() => {
    if (!versionData?.version) return;

    const backendVersion = versionData.version;
    setLatestVersion(backendVersion);

    // Get stored version from localStorage
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

    if (!storedVersion) {
      // First time - save the version
      localStorage.setItem(VERSION_STORAGE_KEY, backendVersion);
      setCurrentVersion(backendVersion);
      setNewVersionAvailable(false);
    } else {
      setCurrentVersion(storedVersion);

      // Compare versions
      if (storedVersion !== backendVersion) {
        // New version detected - update localStorage and force reload
        localStorage.setItem(VERSION_STORAGE_KEY, backendVersion);

        // Force reload with cache clearing for PWA (iOS/Android standalone)
        // Add timestamp to URL to bypass cache
        const url = new URL(window.location.href);
        url.searchParams.set('_v', backendVersion);
        window.location.href = url.toString();
      } else {
        setNewVersionAvailable(false);
      }
    }
  }, [versionData]);

  return {
    newVersionAvailable,
    currentVersion,
    latestVersion,
  };
};
